/**
 * Re-fitting clips when the project canvas changes size.
 *
 * Two coordinate conventions live in the project and a canvas resize has to
 * treat them differently. Both are stated in the RENDERER CONVENTION note at
 * the top of ./layout-presets.ts and were re-verified against
 * packages/core/src/video/video-engine.ts (drawFrameToContext) and
 * packages/core/src/text/title-engine.ts (renderTextClip):
 *
 *   Timeline clips (track.clips): `transform.position` is a PIXEL OFFSET from
 *     the frame centre, so it means a different place on a canvas of a
 *     different size and must be rescaled per axis.
 *   Overlay clips (textClips, shapeClips, svgClips, stickerClips): position is
 *     NORMALIZED 0..1 (`position.x * canvasWidth`), so it already means the
 *     same place on any canvas and must be left alone.
 *
 * What re-fitting deliberately does NOT do: zoom clips to fill the new frame.
 * `transform.scale` is a multiplier on a draw size the renderer recomputes from
 * the live canvas for every frame, so a clip at scale 1 stays fitted to the
 * canvas on its own. Keeping the stored scale therefore keeps the user's zoom
 * "relative to fit", and a 16:9 shot on a 9:16 canvas letterboxes instead of
 * being centre-cropped. That is the reversible answer: cover-filling would
 * throw away about two thirds of the width of every landscape shot with no way
 * to know whether the subject survived the crop. Timeline.backgroundFillMode
 * already treats the letterbox as a designed state. Callers that want the crop
 * can set fitMode "cover" per clip afterwards, as a separate undoable step.
 *
 * The one scale that must move is a clip confined to a `layoutSlot`: that scale
 * was derived from the slot rectangle and the frame aspect, so it is recomputed
 * from the same slot against the new frame.
 *
 * Every factor here is a plain ratio between the two canvas sizes, so applying
 * the resize in reverse undoes it (to within floating point rounding). That is
 * what lets a single history entry carry the settings change and the re-fit.
 */

import type { Keyframe, Transform } from "../types/timeline";
import type { Project } from "../types/project";
import { layoutSlotToTransform } from "./layout-presets";

export interface CanvasSize {
  readonly width: number;
  readonly height: number;
}

/** Anything carrying a transform the resize can act on. */
export interface RefittableClip {
  readonly transform: Transform;
  readonly keyframes?: readonly Keyframe[];
}

/** Multipliers a canvas change implies for one clip's stored transform. */
interface RefitFactors {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
}

/**
 * Clip keyframe properties that hold a transform value in canvas terms. The
 * short names are what the timeline writes; the "transform."-prefixed names
 * belong to motion compositions, which have their own frame and are untouched.
 */
const KEYFRAME_FACTORS: Record<string, keyof RefitFactors> = {
  "position.x": "x",
  "position.y": "y",
  "scale.x": "scale",
  "scale.y": "scale",
};

function isUsableSize(size: CanvasSize | undefined): size is CanvasSize {
  return (
    !!size &&
    Number.isFinite(size.width) &&
    Number.isFinite(size.height) &&
    size.width > 0 &&
    size.height > 0
  );
}

export function canvasSizesMatch(a: CanvasSize, b: CanvasSize): boolean {
  return a.width === b.width && a.height === b.height;
}

function isIdentity(factors: RefitFactors): boolean {
  return factors.x === 1 && factors.y === 1 && factors.scale === 1;
}

/**
 * The scale a slot-confined clip needs on the new frame, over the one it needed
 * on the old frame. Reusing layoutSlotToTransform keeps this in step with the
 * layout presets: whatever they would write for the new frame is what a
 * resized project ends up with, and any zoom the user added on top survives as
 * the ratio between the two.
 */
function slotScaleRatio(
  slot: NonNullable<Transform["layoutSlot"]>,
  from: CanvasSize,
  to: CanvasSize,
  sourceAspect: number | undefined,
): number {
  const target = { x: slot.x, y: slot.y, w: slot.width, h: slot.height, label: "" };
  const before = layoutSlotToTransform(target, from, sourceAspect).scale.x;
  const after = layoutSlotToTransform(target, to, sourceAspect).scale.x;
  if (!(before > 0) || !Number.isFinite(after)) return 1;
  return after / before;
}

function frameFactors(
  transform: Transform,
  from: CanvasSize,
  to: CanvasSize,
  sourceAspect: number | undefined,
): RefitFactors {
  const slot = transform.layoutSlot;
  return {
    x: to.width / from.width,
    y: to.height / from.height,
    scale: slot ? slotScaleRatio(slot, from, to, sourceAspect) : 1,
  };
}

/**
 * Overlays are drawn at fixed pixel sizes (font size, shape size) onto a canvas
 * of the project size, so their apparent size is a fraction of the frame. The
 * width ratio is what keeps that fraction: text that wrapped at 80% of the
 * frame still wraps at 80% of it, in either direction.
 */
function overlayFactors(from: CanvasSize, to: CanvasSize): RefitFactors {
  return { x: 1, y: 1, scale: to.width / from.width };
}

function scaleTransform(transform: Transform, factors: RefitFactors): Transform {
  if (isIdentity(factors)) return transform;
  return {
    ...transform,
    position: {
      x: transform.position.x * factors.x,
      y: transform.position.y * factors.y,
    },
    scale: {
      x: transform.scale.x * factors.scale,
      y: transform.scale.y * factors.scale,
    },
  };
}

function scaleKeyframes(
  keyframes: readonly Keyframe[],
  factors: RefitFactors,
): readonly Keyframe[] {
  if (isIdentity(factors)) return keyframes;

  let changed = false;
  const next = keyframes.map((keyframe) => {
    const key = KEYFRAME_FACTORS[keyframe.property];
    if (!key || typeof keyframe.value !== "number") return keyframe;
    const factor = factors[key];
    if (factor === 1) return keyframe;
    changed = true;
    return { ...keyframe, value: keyframe.value * factor };
  });

  return changed ? next : keyframes;
}

function applyFactors<T extends RefittableClip>(clip: T, factors: RefitFactors): T {
  if (isIdentity(factors)) return clip;

  const transform = scaleTransform(clip.transform, factors);
  if (!clip.keyframes || clip.keyframes.length === 0) {
    return { ...clip, transform };
  }

  const keyframes = scaleKeyframes(clip.keyframes, factors);
  return keyframes === clip.keyframes
    ? { ...clip, transform }
    : { ...clip, transform, keyframes };
}

/**
 * Re-fit a clip whose position is a pixel offset from the frame centre.
 * `sourceAspect` is only consulted for slot-confined clips; without it the slot
 * scale falls back to the frame-independent one layoutSlotToTransform uses, and
 * the stored scale is left alone.
 */
export function refitFrameClip<T extends RefittableClip>(
  clip: T,
  from: CanvasSize,
  to: CanvasSize,
  sourceAspect?: number,
): T {
  if (!isUsableSize(from) || !isUsableSize(to) || canvasSizesMatch(from, to)) {
    return clip;
  }
  return applyFactors(clip, frameFactors(clip.transform, from, to, sourceAspect));
}

/** Re-fit a clip whose position is normalized 0..1 over the frame. */
export function refitOverlayClip<T extends RefittableClip>(
  clip: T,
  from: CanvasSize,
  to: CanvasSize,
): T {
  if (!isUsableSize(from) || !isUsableSize(to) || canvasSizesMatch(from, to)) {
    return clip;
  }
  return applyFactors(clip, overlayFactors(from, to));
}

const OVERLAY_FIELDS = [
  "textClips",
  "shapeClips",
  "svgClips",
  "stickerClips",
] as const;

function sourceAspectsByMediaId(project: Project): Map<string, number> {
  const aspects = new Map<string, number>();
  for (const item of project.mediaLibrary?.items ?? []) {
    const width = item.metadata?.width ?? 0;
    const height = item.metadata?.height ?? 0;
    if (width > 0 && height > 0) aspects.set(item.id, width / height);
  }
  return aspects;
}

/**
 * Move every clip so the project looks the same on the new canvas as it did on
 * the old one. Mutates `project` in place, the way the action executor's other
 * project cases do. Returns whether anything moved.
 */
export function applyCanvasResize(
  project: Project,
  from: CanvasSize,
  to: CanvasSize,
): boolean {
  if (!isUsableSize(from) || !isUsableSize(to) || canvasSizesMatch(from, to)) {
    return false;
  }

  let changed = false;
  const aspects = sourceAspectsByMediaId(project);

  const tracks = project.timeline.tracks.map((track) => {
    let trackChanged = false;
    const clips = track.clips.map((clip) => {
      const next = refitFrameClip(clip, from, to, aspects.get(clip.mediaId));
      if (next !== clip) trackChanged = true;
      return next;
    });
    if (!trackChanged) return track;
    changed = true;
    return { ...track, clips };
  });

  if (changed) {
    (project as { timeline: Project["timeline"] }).timeline = {
      ...project.timeline,
      tracks,
    };
  }

  const overlays = project as unknown as Record<string, RefittableClip[] | undefined>;
  for (const field of OVERLAY_FIELDS) {
    const clips = overlays[field];
    if (!clips || clips.length === 0) continue;

    let fieldChanged = false;
    const next = clips.map((clip) => {
      const refitted = refitOverlayClip(clip, from, to);
      if (refitted !== clip) fieldChanged = true;
      return refitted;
    });
    if (!fieldChanged) continue;

    overlays[field] = next;
    changed = true;
  }

  return changed;
}
